import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type {
  CreateShowRequest,
  ShowSummary,
  ShowType,
  TipSummary,
} from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t, getLocale } from '@fetmode/i18n';
import { ConfirmModal } from '@fetmode/ui';
import { isPublicMainStage } from '@fetmode/utils';

import type { RoxyCommand } from './parse-roxy-command.js';
import { isRoxyUtterance } from './parse-roxy-command.js';
import { useRoxy } from '../roxy/roxy-ui.js';
import { requestStudioCameraCycle } from './roxy-prefs.js';
import { useRoxyListener } from './useRoxyListener.js';

export interface RoxyBroadcastHostProps {
  liveShow: ShowSummary | null;
  isSubmitting: boolean;
  recentTips: readonly TipSummary[];
  onStartShow: (type: ShowType, options?: Omit<CreateShowRequest, 'type'>) => Promise<void>;
  onEndShow: () => Promise<void>;
  onReturnToPublic: () => Promise<void>;
  onShowUpdated: (show: ShowSummary) => void;
  onError: (message: string | null) => void;
}

type PendingConfirm = {
  title: string;
  message: string;
  confirmLabel: string;
  run: () => Promise<void>;
};

export function shouldSkipCaptionForRoxy(text: string): boolean {
  return isRoxyUtterance(text);
}

export function RoxyBroadcastHost({
  liveShow,
  isSubmitting,
  recentTips,
  onStartShow,
  onEndShow,
  onReturnToPublic,
  onShowUpdated,
  onError,
}: RoxyBroadcastHostProps): ReactElement {
  const { api } = useAuth();
  const { listenEnabled, reportListening } = useRoxy();
  const [thank, setThank] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!thank) {
      return;
    }
    const timer = window.setTimeout(() => setThank(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [thank]);

  const runGuarded = useCallback(
    async (work: () => Promise<void>): Promise<void> => {
      if (isSubmitting || busy) {
        return;
      }
      setBusy(true);
      try {
        await work();
      } catch (err) {
        onError(err instanceof ApiError ? err.message : t('common.studio.live.roxyUnable'));
      } finally {
        setBusy(false);
      }
    },
    [busy, isSubmitting, onError],
  );

  const handleCommand = useCallback(
    (command: RoxyCommand) => {
      void (async () => {
        if (command.kind === 'switchCamera') {
          requestStudioCameraCycle();
          return;
        }

        if (command.kind === 'thankTopTipper') {
          const top = recentTips[0];
          setThank(top?.fromUsername ?? t('common.studio.live.roxyTopTipperFallback'));
          return;
        }

        if (command.kind === 'thank') {
          setThank(command.name);
          return;
        }

        if (command.kind === 'startPublic') {
          if (liveShow && isPublicMainStage(liveShow.type)) {
            return;
          }
          await runGuarded(() => onStartShow('MAIN_STAGE'));
          return;
        }

        if (command.kind === 'endPrivate') {
          if (liveShow && !isPublicMainStage(liveShow.type)) {
            await runGuarded(onReturnToPublic);
          }
          return;
        }

        if (command.kind === 'startParty') {
          const start = async (): Promise<void> => {
            if (liveShow) {
              const result = await api.transitionShow(liveShow.id, { toType: 'PARTY' });
              onShowUpdated(result.show);
              return;
            }
            await onStartShow('PARTY');
          };
          if ((liveShow?.viewerCount ?? 0) > 0) {
            setPending({
              title: t('common.studio.live.roxyConfirmPartyTitle'),
              message: t('common.studio.live.roxyConfirmParty'),
              confirmLabel: t('common.studio.live.roxyConfirm'),
              run: start,
            });
            return;
          }
          await runGuarded(start);
          return;
        }

        if (command.kind === 'startPrivate') {
          const start = async (): Promise<void> => {
            const pricing = await api.getModelShowPricing();
            const toType = pricing.pricing.defaultQueueType;
            if (liveShow) {
              const result = await api.transitionShow(liveShow.id, { toType });
              onShowUpdated(result.show);
              return;
            }
            await onStartShow(toType);
          };
          setPending({
            title: t('common.studio.live.roxyConfirmPrivateTitle'),
            message: t('common.studio.live.roxyConfirmPrivate'),
            confirmLabel: t('common.studio.live.roxyConfirm'),
            run: start,
          });
          return;
        }

        if (command.kind === 'endShow') {
          if (!liveShow) {
            return;
          }
          setPending({
            title: t('common.studio.live.roxyConfirmEndTitle'),
            message: t('common.studio.live.roxyConfirmEnd'),
            confirmLabel: t('common.studio.live.endShow'),
            run: onEndShow,
          });
          return;
        }

        if (command.kind === 'setPrivate') {
          await runGuarded(async () => {
            const pricing = await api.getModelShowPricing();
            const exclusive =
              liveShow?.type === 'EXCLUSIVE' ||
              (!liveShow && pricing.pricing.defaultQueueType === 'EXCLUSIVE');
            await api.updateModelShowPricing(
              exclusive
                ? { exclusiveCoinsPerMinute: command.coins }
                : { vipCoinsPerMinute: command.coins },
            );
          });
          return;
        }

        if ((command.kind === 'mute' || command.kind === 'ban') && liveShow) {
          const audience = await api.listShowAudience(liveShow.id);
          const needle = command.name.toLowerCase();
          const matches = audience.members.filter((member) => {
            const username = member.username.toLowerCase();
            return username === needle || username.includes(needle);
          });
          const member =
            matches.find((row) => row.username.toLowerCase() === needle) ??
            (matches.length === 1 ? matches[0] : undefined);
          if (!member) {
            onError(t('common.studio.live.roxyUserMissing').replace('{{name}}', command.name));
            return;
          }
          if (command.kind === 'mute') {
            await runGuarded(async () => {
              await api.muteShowUser(liveShow.id, { userId: member.userId });
            });
            return;
          }
          setPending({
            title: t('common.studio.live.roxyConfirmBanTitle'),
            message: t('common.studio.live.roxyConfirmBan').replace(
              '{{name}}',
              member.username,
            ),
            confirmLabel: t('common.studio.live.roxyConfirm'),
            run: async () => {
              await api.banShowUser(liveShow.id, { userId: member.userId });
            },
          });
        }
      })();
    },
    [
      api,
      liveShow,
      onEndShow,
      onError,
      onReturnToPublic,
      onShowUpdated,
      onStartShow,
      recentTips,
      runGuarded,
    ],
  );

  const { listening } = useRoxyListener({
    enabled: listenEnabled,
    lang: getLocale() === 'es' ? 'es-ES' : 'en-US',
    onCommand: handleCommand,
  });

  useEffect(() => {
    reportListening(listening);
    return () => {
      reportListening(false);
    };
  }, [listening, reportListening]);

  return (
    <>
      {thank ? (
        <p className="live-controls__roxy-thank" aria-live="polite">
          {t('common.studio.live.roxyThank').replace('{{name}}', thank)}
        </p>
      ) : null}
      <ConfirmModal
        open={pending !== null}
        title={pending?.title ?? ''}
        message={pending?.message ?? ''}
        confirmLabel={pending?.confirmLabel ?? t('common.studio.live.roxyConfirm')}
        cancelLabel={t('common.studio.live.roxyCancel')}
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          const next = pending;
          setPending(null);
          if (next) {
            void runGuarded(next.run);
          }
        }}
      />
    </>
  );
}
